import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface FaqOptimizeSettingsProps {
  rateThreshold: number;
  minNotHelpful: number;
  saving: boolean;
  onSave: (rateThreshold: number, minNotHelpful: number) => void;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

export default function FaqOptimizeSettings({
  rateThreshold,
  minNotHelpful,
  saving,
  onSave,
}: FaqOptimizeSettingsProps) {
  const { t } = useTranslation();
  const [rate, setRate] = useState(String(Math.round(rateThreshold * 100)));
  const [min, setMin] = useState(String(minNotHelpful));

  useEffect(() => {
    setRate(String(Math.round(rateThreshold * 100)));
    setMin(String(minNotHelpful));
  }, [rateThreshold, minNotHelpful]);

  const parsedRate = Math.min(100, Math.max(0, Number(rate)));
  const parsedMin = Math.min(999, Math.max(0, Math.round(Number(min))));

  const submit = () => {
    if (!Number.isFinite(parsedRate) || !Number.isFinite(parsedMin)) return;
    onSave(parsedRate / 100, parsedMin);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-equalizer-line text-lg" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("helpFeedback.settingsTitle")}</h2>
            <p className="mt-1 max-w-xl text-xs leading-relaxed text-foreground-500">{t("helpFeedback.settingsSub")}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:max-w-xl">
        <div>
          <label htmlFor="faq-rate-threshold" className="mb-1.5 block text-sm font-medium text-foreground-800">
            {t("helpFeedback.settingsRateLabel")}
          </label>
          <div className="relative">
            <input
              id="faq-rate-threshold"
              type="number"
              min={0}
              max={100}
              value={rate}
              onChange={(event) => setRate(event.target.value)}
              className={`${inputClass} pr-9`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400">%</span>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-foreground-500">{t("helpFeedback.settingsRateHint")}</p>
        </div>
        <div>
          <label htmlFor="faq-min-not-helpful" className="mb-1.5 block text-sm font-medium text-foreground-800">
            {t("helpFeedback.settingsMinLabel")}
          </label>
          <input
            id="faq-min-not-helpful"
            type="number"
            min={0}
            max={999}
            value={min}
            onChange={(event) => setMin(event.target.value)}
            className={inputClass}
          />
          <p className="mt-1 text-[11px] leading-relaxed text-foreground-500">{t("helpFeedback.settingsMinHint")}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
          </span>
          {saving ? t("helpFeedback.settingsSaving") : t("helpFeedback.settingsSave")}
        </button>
        <span className="text-xs text-foreground-500">
          {t("helpFeedback.settingsCurrent", { rate: Math.round(rateThreshold * 100), min: minNotHelpful })}
        </span>
      </div>
    </div>
  );
}